/**
 * PID Controller for smooth budget adjustments
 */
class PIDController {
  private integral = 0;
  private previousError = 0;
  private readonly kp: number; // Proportional gain
  private readonly ki: number; // Integral gain
  private readonly kd: number; // Derivative gain

  constructor(kp = 0.12, ki = 0.03, kd = 0.04) {
    this.kp = kp;
    this.ki = ki;
    this.kd = kd;
  }

  calculate(error: number, dt: number): number {
    // Update integral with anti-windup (clamp to prevent excessive buildup)
    this.integral = Math.max(-5, Math.min(5, this.integral + error * dt));

    // Calculate derivative
    const derivative = dt > 0 ? (error - this.previousError) / dt : 0;

    // Calculate output
    const output = this.kp * error + this.ki * this.integral + this.kd * derivative;

    this.previousError = error;
    return output;
  }

  reset(): void {
    this.integral = 0;
    this.previousError = 0;
  }
}

/**
 * Dynamic Point Budget Manager
 *
 * Automatically adjusts point budget to target monitor refresh rate using:
 * - Automatic refresh rate detection via requestAnimationFrame
 * - Per-frame timing for accurate FPS measurement
 * - PID controller for smooth, responsive adjustments
 * - Adaptive targeting with headroom for stable performance
 */
export class DynamicPointBudget {
  private frameTimings: number[] = [];
  private readonly timingHistorySize: number = 120; // Track last 120 frames (~2 seconds at 60fps)
  private detectedRefreshRate: number | null = null;
  private targetFPS = 60; // Default target, will be updated based on detection
  private readonly fpsHeadroom: number = 0.75; // Target 75% of refresh rate for stability
  private minPointBudget: number;
  private maxPointBudget: number;
  private pidController: PIDController;
  private lastFrameTime: number | null = null;
  private lastAdjustmentTime = 0;
  private readonly adjustmentInterval: number = 500; // Adjust every 500ms for smooth gradual changes
  private stabilityCounter = 0;
  private readonly stabilityThreshold: number = 20; // Frames of stable performance before increasing budget
  private fpsCapDetected: number | null = null; // Detected hard FPS cap (browser/vsync)
  private fpsCapSamples = 0;
  private previousMedianFPS: number[] = []; // Track FPS history to detect plateaus

  constructor(minBudget = 3000000, maxBudget = 10000000) {
    this.minPointBudget = minBudget;
    this.maxPointBudget = maxBudget;
    this.pidController = new PIDController();
    this.detectRefreshRate();
  }

  /**
   * Detect monitor refresh rate by measuring frame intervals
   */
  private detectRefreshRate(): void {
    const samples: number[] = [];
    let lastTime = performance.now();
    let sampleCount = 0;
    const maxSamples = 60;

    const measure = (currentTime: number) => {
      const delta = currentTime - lastTime;
      if (delta > 0 && delta < 100) {
        // Valid frame time (between ~10fps and instant)
        samples.push(1000 / delta);
        sampleCount++;
      }
      lastTime = currentTime;

      if (sampleCount < maxSamples) {
        requestAnimationFrame(measure);
      } else {
        // Calculate median FPS as refresh rate
        samples.sort((a, b) => a - b);
        const median = samples[Math.floor(samples.length / 2)];

        // Round to nearest common refresh rate
        const commonRates = [30, 60, 75, 90, 120, 144, 165, 240];
        this.detectedRefreshRate = commonRates.reduce((closest, rate) => (Math.abs(rate - median) < Math.abs(closest - median) ? rate : closest));

        this.targetFPS = Math.min(60, this.detectedRefreshRate * this.fpsHeadroom);
      }
    };

    requestAnimationFrame(measure);
  }

  /**
   * Record actual FPS from the viewer
   */
  recordFPS(fps: number): void {
    if (fps > 0) {
      this.frameTimings.push(fps);

      // Keep only recent samples
      if (this.frameTimings.length > 60) {
        this.frameTimings.shift();
      }
    }
  }

  /**
   * Calculate current FPS from recorded samples
   */
  private getCurrentFPS(): number {
    if (this.frameTimings.length < 3) {
      return 0; // Not enough data
    }

    // Use median FPS to avoid outlier influence
    const sorted = [...this.frameTimings].sort((a, b) => a - b);
    const medianFPS = sorted[Math.floor(sorted.length / 2)];

    return medianFPS;
  }

  /**
   * Get the recommended point budget adjustment using PID controller
   * Returns null if no adjustment is needed yet
   */
  getRecommendedBudget(currentBudget: number, currentFPS: number, timestamp: number, visiblePoints = 0): number | null {
    // Record the FPS sample
    this.recordFPS(currentFPS);

    // Check if enough time has passed since last adjustment
    if (timestamp - this.lastAdjustmentTime < this.adjustmentInterval) {
      return null;
    }

    // Use median FPS from recent samples for stability
    const medianFPS = this.getCurrentFPS();

    // Skip if no valid FPS data or still detecting refresh rate
    if (medianFPS <= 0 || this.detectedRefreshRate === null) {
      return null;
    }

    // If visible points are significantly less than budget, don't increase budget
    // It means we're not utilizing the current budget, so more won't help
    const utilizationRatio = visiblePoints / currentBudget;
    const isUnderutilized = utilizationRatio < 0.85; // Less than 85% utilization

    // Detect hard FPS cap (browser/vsync limit)
    // A hard cap means FPS cannot exceed a ceiling value despite having GPU headroom
    // Strategy: If FPS is stable/plateaued at high budget, we've hit a hard cap

    // Track median FPS over time to detect plateaus
    this.previousMedianFPS.push(medianFPS);
    if (this.previousMedianFPS.length > 20) {
      this.previousMedianFPS.shift();
    }

    // Only check for caps when we're using significant budget (80%+)
    if (currentBudget > this.maxPointBudget * 0.8 && this.previousMedianFPS.length >= 15) {
      // Check if FPS is plateaued - consistent values with minimal variance
      const recentFPS = this.previousMedianFPS.slice(-15);
      const avgFPS = recentFPS.reduce((a, b) => a + b, 0) / recentFPS.length;
      const variance = recentFPS.reduce((sum, fps) => sum + Math.pow(fps - avgFPS, 2), 0) / recentFPS.length;
      const stdDev = Math.sqrt(variance);

      // Low variance (<3 FPS std dev) indicates a plateau/ceiling
      const isPlateaued = stdDev < 3;

      // Check if FPS is above our target - if plateaued above target, it's a hard cap
      const isAboveTarget = avgFPS > this.targetFPS + 5; // At least 5 FPS above target

      if (isPlateaued && isAboveTarget) {
        this.fpsCapSamples++;
        if (this.fpsCapSamples > 10 && this.fpsCapDetected === null) {
          // Round to nearest common refresh rate
          const commonRates = [30, 60, 75, 90, 120, 144, 165, 240];
          const detectedCap = commonRates.reduce((closest, rate) => (Math.abs(rate - avgFPS) < Math.abs(closest - avgFPS) ? rate : closest));

          this.fpsCapDetected = detectedCap;
          // Target 75% of detected cap for headroom, but cap at 60 FPS for smoothness
          this.targetFPS = Math.min(60, Math.round(detectedCap * this.fpsHeadroom));
        }
      } else {
        this.fpsCapSamples = Math.max(0, this.fpsCapSamples - 1);
      }
    } else {
      this.fpsCapSamples = Math.max(0, this.fpsCapSamples - 1);
    }

    // Calculate error (how far we are from target) using median FPS
    const error = (this.targetFPS - medianFPS) / this.targetFPS; // Normalized error

    // Time since last adjustment in seconds
    const dt = (timestamp - this.lastAdjustmentTime) / 1000;

    // Get PID output (-1 to 1 range typically)
    const pidOutput = this.pidController.calculate(error, dt);

    // Track stability - are we consistently hitting our target?
    const fpsDeviation = Math.abs(this.targetFPS - medianFPS);
    if (fpsDeviation <= 3.0) {
      // Within 3 FPS of target = stable
      this.stabilityCounter++;
      // If we're in the tolerance band, don't adjust
      return null;
    } else {
      this.stabilityCounter = 0;
    }

    // Calculate new budget based on PID output
    // When error > 0: FPS < target = GPU is struggling = DECREASE budget
    // When error < 0: FPS > target = GPU has headroom = INCREASE budget
    let adjustment = -pidOutput; // INVERT: PID gives positive when low FPS, we want negative adjustment

    if (error > 0) {
      // FPS too low - GPU struggling - DECREASE budget gently
      adjustment = Math.max(-0.02, Math.min(-0.01, adjustment)); // 1-2% decrease per adjustment
    } else {
      // FPS above target - GPU has headroom - might be able to INCREASE budget
      // But ONLY if we're actually using the current budget
      if (isUnderutilized) {
        // Don't increase if we're not using what we have
        return null;
      }

      // Be very conservative about increasing to avoid dropping below target
      if (this.stabilityCounter < this.stabilityThreshold) {
        adjustment = Math.min(0.02, Math.max(0.005, adjustment)); // 0.5-2% increase
      } else {
        adjustment = Math.min(0.05, Math.max(0.01, adjustment)); // 1-5% increase after stability
      }
    }

    const newBudget = Math.round(currentBudget * (1 + adjustment));

    // Clamp to bounds
    const clampedBudget = Math.max(this.minPointBudget, Math.min(this.maxPointBudget, newBudget));

    // Only adjust if there's a meaningful change (at least 1% or 10k points)
    if (Math.abs(clampedBudget - currentBudget) < Math.max(currentBudget * 0.01, 10000)) {
      return null;
    }

    this.lastAdjustmentTime = timestamp;
    return clampedBudget;
  }

  /**
   * Reset the controller state
   */
  reset(): void {
    this.frameTimings = [];
    this.lastFrameTime = null;
    this.lastAdjustmentTime = 0;
    this.stabilityCounter = 0;
    this.pidController.reset();
    this.detectRefreshRate();
  }

  /**
   * Get current FPS statistics
   */
  getStats(): {
    currentFPS: number;
    targetFPS: number;
    refreshRate: number | null;
    frameCount: number;
    stability: number;
  } {
    return {
      currentFPS: this.getCurrentFPS(),
      targetFPS: this.targetFPS,
      refreshRate: this.detectedRefreshRate,
      frameCount: this.frameTimings.length,
      stability: this.stabilityCounter,
    };
  }

  /**
   * Get the detected refresh rate
   */
  getRefreshRate(): number | null {
    return this.detectedRefreshRate;
  }

  /**
   * Get the target FPS
   */
  getTargetFPS(): number {
    return this.targetFPS;
  }
}
